import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { PlusIcon, TrashIcon, PencilIcon, CheckIcon, CloseIcon } from '@components/icons';
import {
  readingLevelToDisplayText,
  writingLevelToDisplayText,
  READING_LEVELS,
  WRITING_LEVELS,
  FULL_READING_LEVEL,
  FULL_WRITING_LEVEL,
  LEARNING_READING_LEVEL,
  LEARNING_WRITING_LEVEL,
  isLearningLevels,
} from '@skjv/common';
import type { ReadingLevel, WritingLevel, Student } from '@skjv/common';

/**
 * Student list - CRUD for students across all study groups.
 */
export const StudentList = observer(function StudentList() {
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [newStudentName, setNewStudentName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [newIsLearning, setNewIsLearning] = useState(false);
  const [newReadingLevel, setNewReadingLevel] = useState<ReadingLevel>(LEARNING_READING_LEVEL);
  const [newWritingLevel, setNewWritingLevel] = useState<WritingLevel>(LEARNING_WRITING_LEVEL);

  const studyGroups = bibleState.studyGroups;

  // With a single group there is nothing to choose — preselect it
  useEffect(() => {
    if (!selectedGroupId && studyGroups.length === 1) {
      setSelectedGroupId(studyGroups[0].id);
    }
  }, [studyGroups, selectedGroupId]);

  // Get all students with their group info
  const allStudents: Array<{ student: Student; groupId: string; groupName: string }> = [];
  for (const group of studyGroups) {
    for (const student of group.students) {
      allStudents.push({ student, groupId: group.id, groupName: group.name });
    }
  }

  const handleAddStudent = () => {
    if (!newStudentName.trim() || !selectedGroupId) return;

    const newStudent: Student = {
      id: crypto.randomUUID(),
      name: newStudentName.trim(),
      readingLevel: newIsLearning ? newReadingLevel : FULL_READING_LEVEL,
      writingLevel: newIsLearning ? newWritingLevel : FULL_WRITING_LEVEL,
    };

    bibleState.addStudentToGroup(selectedGroupId, newStudent);
    setNewStudentName('');
    setNewIsLearning(false);
    setNewReadingLevel(LEARNING_READING_LEVEL);
    setNewWritingLevel(LEARNING_WRITING_LEVEL);
    setIsAddingStudent(false);
  };

  const handleRemoveStudent = (groupId: string, studentId: string) => {
    bibleState.showConfirmDialog({
      title: 'Remove Student',
      message: 'Remove this student from the group?',
      confirmText: 'Remove',
      variant: 'danger',
      onConfirm: () => bibleState.removeStudentFromGroup(groupId, studentId),
    });
  };

  const handleUpdateStudent = (groupId: string, studentId: string, updates: Partial<Student>) => {
    bibleState.updateStudent(groupId, studentId, updates);
  };

  if (studyGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-stone-500 dark:text-stone-400">
        <p className="mb-2">No study groups yet.</p>
        <p className="text-sm">Create a group under the Groups tab above first.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Add student form */}
      {isAddingStudent ? (
        <div className="rounded-xl border-2 border-dashed border-cyan-400 bg-cyan-50/30 p-3 dark:border-cyan-600 dark:bg-cyan-900/20">
          <div className="flex flex-col gap-2">
            <input
              type="text"
              value={newStudentName}
              onChange={(e) => setNewStudentName(e.target.value)}
              placeholder="Student name"
              autoFocus
              className="w-full rounded-lg border border-stone-300 bg-white/50 px-3 py-2 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-800/50 dark:text-stone-200 dark:placeholder-stone-500"
            />
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-800 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200 dark:[color-scheme:dark]"
            >
              <option value="">Select group...</option>
              {studyGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
              <input
                type="checkbox"
                checked={newIsLearning}
                onChange={(e) => setNewIsLearning(e.target.checked)}
                data-testid="learning-toggle"
                className="h-4 w-4 rounded border-stone-300 text-cyan-500 focus:ring-cyan-500"
              />
              Learning to read &amp; write
            </label>
            {newIsLearning && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs text-stone-500 dark:text-stone-400">
                    Reading Level
                  </label>
                  <select
                    value={newReadingLevel}
                    onChange={(e) => setNewReadingLevel(e.target.value as ReadingLevel)}
                    className="w-full rounded-lg border border-stone-300 bg-white px-2 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200 dark:[color-scheme:dark]"
                  >
                    {READING_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {readingLevelToDisplayText(level)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-stone-500 dark:text-stone-400">
                    Writing Level
                  </label>
                  <select
                    value={newWritingLevel}
                    onChange={(e) => setNewWritingLevel(e.target.value as WritingLevel)}
                    className="w-full rounded-lg border border-stone-300 bg-white px-2 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200 dark:[color-scheme:dark]"
                  >
                    {WRITING_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {writingLevelToDisplayText(level)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleAddStudent}
                disabled={!newStudentName.trim() || !selectedGroupId}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-cyan-500 px-3 py-2 text-white transition-colors hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" />
                Add
              </button>
              <button
                onClick={() => {
                  setIsAddingStudent(false);
                  setNewStudentName('');
                }}
                className="rounded-lg px-3 py-2 text-stone-600 transition-colors hover:bg-stone-200/50 dark:text-stone-400 dark:hover:bg-stone-700/30"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAddingStudent(true)}
          className="flex items-center gap-2 rounded-xl border-2 border-dashed border-stone-300 p-3 text-stone-500 transition-all hover:border-cyan-400 hover:text-cyan-600 dark:border-stone-600 dark:text-stone-400 dark:hover:border-cyan-500 dark:hover:text-cyan-400"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add new student</span>
        </button>
      )}

      {/* Students list grouped by study group */}
      {studyGroups.map((group) => (
        <div key={group.id}>
          <h3 className="mb-2 text-sm font-medium text-stone-600 dark:text-stone-400">
            {group.name}
          </h3>
          {group.students.length === 0 ? (
            <p className="pl-2 text-sm text-stone-400 dark:text-stone-500">
              No students in this group
            </p>
          ) : (
            <div className="space-y-2">
              {group.students.map((student) => (
                <StudentRow
                  key={student.id}
                  student={student}
                  groupId={group.id}
                  isEditing={editingStudentId === student.id}
                  onEdit={() => setEditingStudentId(student.id)}
                  onCancelEdit={() => setEditingStudentId(null)}
                  onUpdate={(updates) => handleUpdateStudent(group.id, student.id, updates)}
                  onRemove={() => handleRemoveStudent(group.id, student.id)}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
});

interface StudentRowProps {
  student: Student;
  groupId: string;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onUpdate: (updates: Partial<Student>) => void;
  onRemove: () => void;
}

function StudentRow({
  student,
  isEditing,
  onEdit,
  onCancelEdit,
  onUpdate,
  onRemove,
}: StudentRowProps) {
  const [editName, setEditName] = useState(student.name);
  const [editIsLearning, setEditIsLearning] = useState(
    isLearningLevels(student.readingLevel, student.writingLevel),
  );
  const [editReadingLevel, setEditReadingLevel] = useState<ReadingLevel>(
    isLearningLevels(student.readingLevel, student.writingLevel)
      ? student.readingLevel
      : LEARNING_READING_LEVEL,
  );
  const [editWritingLevel, setEditWritingLevel] = useState<WritingLevel>(
    isLearningLevels(student.readingLevel, student.writingLevel)
      ? student.writingLevel
      : LEARNING_WRITING_LEVEL,
  );

  const handleSave = () => {
    onUpdate({
      name: editName,
      readingLevel: editIsLearning ? editReadingLevel : FULL_READING_LEVEL,
      writingLevel: editIsLearning ? editWritingLevel : FULL_WRITING_LEVEL,
    });
    onCancelEdit();
  };

  if (isEditing) {
    return (
      <div className="rounded-xl border border-cyan-400 bg-cyan-50/30 p-3 dark:border-cyan-600 dark:bg-cyan-900/20">
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="mb-2 w-full rounded border border-stone-300 bg-white/70 px-2 py-1.5 text-stone-800 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-800/50 dark:text-stone-200"
        />
        <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
          <input
            type="checkbox"
            checked={editIsLearning}
            onChange={(e) => setEditIsLearning(e.target.checked)}
            data-testid="learning-toggle"
            className="h-4 w-4 rounded border-stone-300 text-cyan-500 focus:ring-cyan-500"
          />
          Learning to read &amp; write
        </label>
        <div className={`mb-2 grid grid-cols-2 gap-2 ${editIsLearning ? '' : 'hidden'}`}>
          <div>
            <label className="mb-1 block text-xs text-stone-500 dark:text-stone-400">
              Reading Level
            </label>
            <select
              value={editReadingLevel}
              onChange={(e) => setEditReadingLevel(e.target.value as ReadingLevel)}
              className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200 dark:[color-scheme:dark]"
            >
              {READING_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {readingLevelToDisplayText(level)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-stone-500 dark:text-stone-400">
              Writing Level
            </label>
            <select
              value={editWritingLevel}
              onChange={(e) => setEditWritingLevel(e.target.value as WritingLevel)}
              className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200 dark:[color-scheme:dark]"
            >
              {WRITING_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {writingLevelToDisplayText(level)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-cyan-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-cyan-600"
          >
            <CheckIcon className="h-4 w-4" />
            Save
          </button>
          <button
            onClick={onCancelEdit}
            className="rounded-lg px-3 py-1.5 text-sm text-stone-600 transition-colors hover:bg-stone-200/50 dark:text-stone-400 dark:hover:bg-stone-700/30"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="study-row flex items-center justify-between">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-stone-800 dark:text-stone-200">{student.name}</div>
        <div className="text-xs text-stone-500 dark:text-stone-400">
          {isLearningLevels(student.readingLevel, student.writingLevel) ? (
            <>
              Read: {readingLevelToDisplayText(student.readingLevel)} &bull; Write:{' '}
              {writingLevelToDisplayText(student.writingLevel)}
            </>
          ) : (
            'Full assignment'
          )}
        </div>
      </div>
      <div className="ml-2 flex gap-1">
        <button
          onClick={onEdit}
          className="p-1.5 text-stone-400 transition-colors hover:text-cyan-600 dark:hover:text-cyan-400"
          title="Edit"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        <button
          onClick={onRemove}
          className="p-1.5 text-stone-400 transition-colors hover:text-red-500"
          title="Remove"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
